import { Button } from '@/global/components/Button'
import Menu from '@/global/components/MenuV2'
import Grid from '@/global/components/ui/Grid'
import Dialog from '@/global/guides/Dialog'
import { cn } from '@/lib/utils'
import { cpfValido } from '@acme/common'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from '@mui/material'
import logo from 'assets/logo.png'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import { Clock } from 'global/components/Clock'
import { FormField } from 'global/components/Form'
import { Link } from 'global/components/Link'
import { ToggleTheme } from 'global/components/ToggleTheme'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
    AvaliacaoSchema,
    EmpresasFuncionario,
    ErrorHandler,
    Registro,
    funcionario,
} from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'

const schema = z.object({
    cpf: z
        .string()
        .refine(cpfValido, {
            message: 'CPF inválido',
        })
        .transform(value => value.replace(/\D/g, '')),
    empresaId: z.number(),
    senha: AvaliacaoSchema.shape.senha,
})

type BaterPontoForm = z.infer<typeof schema>

export default function BaterPonto() {
    const [empresaId, setEmpresaId] = useState<number>(0)
    const [open, setOpen] = useState(false)
    const [ponto, setPonto] = useState<Registro>()
    const [empresas, setEmpresas] = useState<EmpresasFuncionario[]>([])

    const methods = useForm<BaterPontoForm>({
        resolver: zodResolver(schema),
    })

    const handleClose = () => {
        setOpen(false)
        methods.setValue('senha', '')
    }

    const handleChangeCPF = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        let { value } = event.target

        if ((value = String(value).replace(/[^0-9]/g, '')).length === 11) {
            try {
                const empresas = await funcionario.get<EmpresasFuncionario[]>(
                    'documento/' + value + '/empresas',
                )

                methods.setValue('empresaId', empresas[0].id)
                setEmpresaId(empresas[0].id)
                setEmpresas(empresas)
            } catch (error) {
                methods.setValue('empresaId', 0)
                setEmpresaId(0)
                setEmpresas([])
                console.error(error)
            }
        }
    }

    const handleSubmit = methods.handleSubmit(
        async data => {
            try {
                const ponto = await funcionario.post<BaterPontoForm, Registro>(
                    'bater-ponto',
                    data,
                )
                handleClose()
                setPonto(ponto)
            } catch (error) {
                handleClose()
                methods.setValue('senha', '')
                toast.error((error as ErrorHandler)?.message)
            }
        },
        error => console.log(error),
    )

    return (
        <FormProvider {...methods}>
            <form
                className="h-screen flex justify-center"
                onSubmit={handleSubmit}
            >
                <div className="absolute right-4 top-2">
                    <ToggleTheme />
                </div>
                <div className="h-screen flex justify-center items-center">
                    <div className="flex flex-col gap-6 justify-center items-center">
                        <header className="hidden sm:block">
                            <img
                                className="w-38 filter dark:brightness-80 dark:invert"
                                src={logo}
                                alt="logo"
                                loading="lazy"
                            />
                        </header>
                        <div
                            className={cn(
                                'bg-foreground flex flex-col justify-center items-center',
                                'rounded-2xl p-10 sm:w-96 w-full shadow-lg',
                            )}
                        >
                            <div className="w-full gap-2 flex flex-col justify-center items-center">
                                <Clock className="mb-4 text-3xl" />
                                <FormField
                                    name="cpf"
                                    label="CPF"
                                    placeholder="000.000.000-00"
                                    required
                                    onChange={handleChangeCPF}
                                    options={{
                                        blocks: [3, 3, 3, 2],
                                        delimiters: ['.', '.', '-'],
                                    }}
                                />
                                {empresaId !== 0 ? (
                                    <FormField
                                        name="empresaId"
                                        label="Empresa"
                                        select
                                        disabled={empresas.length < 2}
                                        required
                                    >
                                        {empresas.map(empresa => (
                                            <Menu.Item
                                                key={empresa.id}
                                                value={empresa.id}
                                            >
                                                {empresa.nomeFantasia}
                                            </Menu.Item>
                                        ))}
                                    </FormField>
                                ) : (
                                    <FormField
                                        name="empresaId"
                                        label="Empresa"
                                        disabled
                                    />
                                )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    sx={{ mt: 1 }}
                                    onClick={() => setOpen(true)}
                                >
                                    Bater ponto
                                </Button>
                            </div>
                            <span className="mt-4 text-center text-xs">
                                Registre o ponto com CPF ou faça{' '}
                                <Link href="/">login.</Link>.
                            </span>
                        </div>
                    </div>
                </div>
            </form>
            <DialogBaterPonto
                open={open}
                setOpen={setOpen}
                methods={methods}
                handleSubmit={handleSubmit}
            />
            <DialogPontoRegistrado
                methods={methods}
                ponto={ponto}
                setPonto={setPonto}
            />
        </FormProvider>
    )
}

interface IProps {
    open: boolean
    setOpen: (open: boolean) => void
    methods: ReturnType<typeof useForm<BaterPontoForm>>
    handleSubmit: (data: any) => void
}

function DialogBaterPonto({ open, setOpen, methods, handleSubmit }: IProps) {
    return (
        <Dialog.Root
            open={open}
            onClose={() => {
                setOpen(false)
                methods.setValue('senha', '')
            }}
            height="auto"
            width="30rem"
            maxWidth="xs"
        >
            <Dialog.Title>
                Digite sua senha para registrar o ponto.
            </Dialog.Title>
            <Dialog.Content>
                <TextField
                    {...methods.register('senha')}
                    variant="filled"
                    fullWidth
                    type="password"
                    label="Senha"
                    size="medium"
                    error={!!methods.formState.errors.senha}
                    sx={{
                        mt: 1,
                    }}
                    helperText={
                        methods.formState.errors.senha?.message as string
                    }
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Button
                    variant="contained"
                    onClick={() => handleSubmit(methods.getValues())}
                >
                    Confirmar
                </Button>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

interface IPropsPontoRegistrado {
    ponto: Registro | undefined
    setPonto: (ponto: Registro | undefined) => void
    methods: IProps['methods']
}

function DialogPontoRegistrado({
    ponto,
    setPonto,
    methods,
}: IPropsPontoRegistrado) {
    const resetPonto = () => {
        setPonto(undefined)
        methods.setValue('senha', '')
    }

    return (
        <Dialog.Root
            open={!!ponto}
            onClose={resetPonto}
            height="auto"
            width="30rem"
            maxWidth="xs"
        >
            <Dialog.Title>Ponto registrado com sucesso!</Dialog.Title>
            <Dialog.Content
                sx={{
                    overflowY: 'hidden',
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            variant="filled"
                            fullWidth
                            label="Data"
                            size="medium"
                            disabled
                            value={dayjs(ponto?.data).format('DD/MM/YYYY')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            variant="filled"
                            fullWidth
                            label="Hora"
                            size="medium"
                            disabled
                            value={ponto?.hora}
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton>
                    Fechar
                </Dialog.Actions.SecondaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
